/**
 * Full-screen modal that loads an external URL inside an iframe, with a
 * persistent TipLingo-branded "Back to TipLingo" pill at the top-left.
 *
 * Why: on iOS native (Capacitor) we use SFSafariViewController (Browser.open)
 * which has its own native "Done" button. But in the web/preview, mobile
 * Safari treats programmatic new-tab opens as same-tab nav and hijacks the
 * whole screen — leaving the user with no way back to TipLingo. This modal
 * keeps TipLingo on top so the user can always return.
 *
 * If the airline site blocks iframe embedding (X-Frame-Options or CSP
 * frame-ancestors), we detect the failure after a load timeout and show a
 * "couldn't load in-app — open in a separate tab" fallback button.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";

// Domains we know hard-block iframe embedding (X-Frame-Options DENY / SAMEORIGIN
// or strict frame-ancestors CSP). For these we skip the iframe attempt entirely
// and present the "open in new tab" fallback immediately.
const KNOWN_FRAME_BLOCKERS = [
  "delta.com",
  "aa.com",
  "united.com",
  "americanairlines.com",
  "britishairways.com",
  "lufthansa.com",
  "emirates.com",
  "qatarairways.com",
  "ana.co.jp",
  "jal.co.jp",
  "koreanair.com",
  "singaporeair.com",
  "cathaypacific.com",
  "qantas.com",
  "turkishairlines.com",
  "airfrance.fr",
  "airfrance.com",
  "klm.com",
  "aircanada.com",
  "alaskaair.com",
  "hawaiianairlines.com",
  "jetblue.com",
  "southwest.com",
  "flyfrontier.com",
  "easyjet.com",
  "ryanair.com",
  "jetstar.com",
  "china-airlines.com",
  "bahamasair.com",
  "virginatlantic.com",
  "flyasiana.com",
];

function isLikelyBlocked(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return KNOWN_FRAME_BLOCKERS.some((d) => host === d || host.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

export default function ExternalPageModal({ url, onClose }) {
  const iframeRef = useRef(null);
  const [blocked, setBlocked] = useState(() => isLikelyBlocked(url));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!url || blocked) return;
    const t = setTimeout(() => {
      // If onload never fires after 4s, treat as blocked.
      if (!loaded) setBlocked(true);
    }, 4000);
    return () => clearTimeout(t);
  }, [url, loaded, blocked]);

  // After a load event, double-check that the iframe actually has content.
  // Some sites accept the request but return a blank/zero-height page.
  useEffect(() => {
    if (!loaded || blocked) return;
    const node = iframeRef.current;
    if (!node) return;
    const t = setTimeout(() => {
      try {
        const doc = node.contentDocument;
        // Cross-origin → reading contentDocument throws; that's actually fine
        // (it means the page did load real cross-origin content). The blank
        // case is when contentDocument exists AND has no body content.
        if (doc && (!doc.body || doc.body.children.length === 0)) {
          setBlocked(true);
        }
      } catch {
        /* cross-origin success — leave as loaded */
      }
    }, 500);
    return () => clearTimeout(t);
  }, [loaded, blocked]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-navy/95"
      data-testid="external-modal"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="external-modal-back"
        className="absolute top-4 left-4 z-10 bg-orange text-white rounded-full pl-3 pr-4 py-2 flex items-center gap-1.5 font-jakarta font-extrabold text-sm shadow-lg active:scale-95 transition-transform"
      >
        <ArrowLeft size={18} weight="bold" />
        Back to TipLingo
      </button>

      {blocked ? (
        <div className="absolute inset-0 flex items-center justify-center px-5">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
            <WarningCircle size={36} weight="duotone" color="#E55B2C" className="mx-auto mb-2" />
            <div className="font-jakarta font-extrabold text-navy text-lg mb-1">
              Airline portal can't open in-app
            </div>
            <p className="text-navy-soft text-sm font-jakarta mb-4 leading-snug">
              This airline blocks embedded browsing. Tap below to open it in
              your regular browser — TipLingo will stay open so you can come
              back any time.
            </p>
            <button
              type="button"
              onClick={() => {
                // Synthetic anchor click — most reliable cross-platform way
                // to trigger a real new tab from JS.
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                document.body.appendChild(a);
                a.click();
                a.remove();
                onClose?.();
              }}
              className="block w-full bg-orange text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              data-testid="external-modal-open-tab"
            >
              <ArrowSquareOut size={16} weight="bold" /> Open in browser
            </button>
            <button
              type="button"
              onClick={onClose}
              className="block w-full mt-2 text-navy-soft font-jakarta font-bold text-sm py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={url}
          title="Airline claim portal"
          onLoad={() => setLoaded(true)}
          onError={() => setBlocked(true)}
          className="absolute inset-0 w-full h-full bg-white"
          // Iframe needs broad permissions for real airline portals to work.
          referrerPolicy="no-referrer"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
        />
      )}
    </div>
  );
}
